// Viewer toolbar icons, inlined from `public/icons/*.svg`.
//
// Each SVG draws the whole 40x40 white button (rounded corners plus the design's
// drop shadow) on a 44x44 canvas, so the artwork sits 2px inside on every edge.
// They are inlined rather than loaded by URL for the same reason as
// `CheckBadge`: the widget runs on third-party pages where the asset base URL or
// a strict CSP would break an <img>.

import type { PropsWithChildren } from "react";

type Props = {
  className: string;
};

/**
 * The design's drop shadow under the white button plate. One definition for all
 * three icons, parameterised by the id each `<g filter="url(#id)">` points at —
 * the filter primitives were byte-identical in every icon.
 */
function ToolbarIconShadow({ id }: { id: string }) {
  return (
    <defs className="toolbar-icon__defs">
      <filter
        className="toolbar-icon__filter"
        id={id}
        x="0"
        y="0"
        width="44"
        height="44"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood
          className="toolbar-icon__filter-flood"
          floodOpacity="0"
          result="BackgroundImageFix"
        />
        <feColorMatrix
          className="toolbar-icon__filter-alpha"
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset className="toolbar-icon__filter-offset" dy="2" />
        <feGaussianBlur
          className="toolbar-icon__filter-blur"
          stdDeviation="1"
        />
        <feComposite
          className="toolbar-icon__filter-composite"
          in2="hardAlpha"
          operator="out"
        />
        <feColorMatrix
          className="toolbar-icon__filter-tint"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"
        />
        <feBlend
          className="toolbar-icon__filter-blend"
          mode="normal"
          in2="BackgroundImageFix"
          result="shadow"
        />
        <feBlend
          className="toolbar-icon__filter-blend toolbar-icon__filter-blend--shape"
          mode="normal"
          in="SourceGraphic"
          in2="shadow"
          result="shape"
        />
      </filter>
    </defs>
  );
}

/** The 44x44 canvas, the shadowed group and the white plate every icon shares. */
function ToolbarIconFrame({
  className,
  filterId,
  children,
}: PropsWithChildren<{ className: string; filterId: string }>) {
  return (
    <svg
      className={["toolbar-icon", className].filter(Boolean).join(" ")}
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g className="toolbar-icon__group" filter={`url(#${filterId})`}>
        <rect
          className="toolbar-icon__plate"
          x="2"
          width="40"
          height="40"
          rx="8"
          fill="white"
        />
        {children}
      </g>
      <ToolbarIconShadow id={filterId} />
    </svg>
  );
}

export function DownloadModelIcon(props: Props) {
  return (
    <ToolbarIconFrame
      className={props.className}
      filterId="sk-icon-download-model"
    >
      <rect
        className="toolbar-icon__plate-outline"
        x="2.5"
        y="0.5"
        width="39"
        height="39"
        rx="7.5"
        stroke="white"
      />
      <path
        className="toolbar-icon__glyph"
        d="M14 27V29C14 29.5304 14.2107 30.0391 14.5858 30.4142C14.9609 30.7893 15.4696 31 16 31H28C28.5304 31 29.0391 30.7893 29.4142 30.4142C29.7893 30.0391 30 29.5304 30 29V27M27 22L22 27L17 22M22 27V16.5"
        stroke="#111111"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        className="toolbar-icon__glyph toolbar-icon__glyph--label"
        d="M17.9385 8.75684H18.7393C18.9443 8.75684 19.112 8.72103 19.2422 8.64941C19.3757 8.57454 19.4749 8.47038 19.54 8.33691C19.6051 8.2002 19.6377 8.03906 19.6377 7.85352C19.6377 7.71029 19.6068 7.57845 19.5449 7.45801C19.4863 7.33757 19.3952 7.24154 19.2715 7.16992C19.1478 7.09505 18.9899 7.05762 18.7979 7.05762C18.6676 7.05762 18.5407 7.08529 18.417 7.14062C18.2933 7.19271 18.1908 7.26758 18.1094 7.36523C18.0312 7.46289 17.9922 7.58171 17.9922 7.72168H16.3418C16.3418 7.31803 16.4525 6.97298 16.6738 6.68652C16.8984 6.39681 17.193 6.17546 17.5576 6.02246C17.9255 5.86621 18.321 5.78809 18.7441 5.78809C19.2487 5.78809 19.6914 5.86621 20.0723 6.02246C20.4531 6.17546 20.7493 6.40332 20.9609 6.70605C21.1758 7.00553 21.2832 7.37663 21.2832 7.81934C21.2832 8.06348 21.2262 8.29297 21.1123 8.50781C20.9984 8.72266 20.8389 8.91146 20.6338 9.07422C20.4287 9.23698 20.1878 9.36556 19.9111 9.45996C19.6377 9.55111 19.3382 9.59668 19.0127 9.59668H17.9385V8.75684ZM17.9385 9.98242V9.16211H19.0127C19.3675 9.16211 19.6914 9.2028 19.9844 9.28418C20.2773 9.3623 20.5296 9.47949 20.7412 9.63574C20.9528 9.79199 21.1156 9.98568 21.2295 10.2168C21.3434 10.4447 21.4004 10.7067 21.4004 11.0029C21.4004 11.335 21.3337 11.6312 21.2002 11.8916C21.0667 12.152 20.8796 12.3717 20.6387 12.5508C20.3978 12.7298 20.1162 12.8665 19.7939 12.9609C19.4717 13.0521 19.1217 13.0977 18.7441 13.0977C18.4479 13.0977 18.1517 13.0586 17.8555 12.9805C17.5592 12.8991 17.2891 12.7754 17.0449 12.6094C16.8008 12.4401 16.6038 12.2253 16.4541 11.9648C16.3076 11.7012 16.2344 11.387 16.2344 11.0225H17.8848C17.8848 11.1722 17.9255 11.3089 18.0068 11.4326C18.0915 11.5531 18.2038 11.6491 18.3438 11.7207C18.4837 11.7923 18.6351 11.8281 18.7979 11.8281C18.9997 11.8281 19.1706 11.7907 19.3105 11.7158C19.4538 11.6377 19.5628 11.5335 19.6377 11.4033C19.7126 11.2731 19.75 11.1283 19.75 10.9688C19.75 10.7279 19.7093 10.5358 19.6279 10.3926C19.5498 10.2493 19.4342 10.1452 19.2812 10.0801C19.1315 10.015 18.9508 9.98242 18.7393 9.98242H17.9385ZM24.6523 13H23.0752L23.085 11.6816H24.6523C24.9779 11.6816 25.2529 11.6035 25.4775 11.4473C25.7054 11.291 25.8779 11.0583 25.9951 10.749C26.1156 10.4365 26.1758 10.0524 26.1758 9.59668V9.28906C26.1758 8.94727 26.1416 8.64779 26.0732 8.39062C26.0081 8.13021 25.9105 7.91374 25.7803 7.74121C25.6501 7.56543 25.4873 7.43359 25.292 7.3457C25.0999 7.25781 24.8786 7.21387 24.6279 7.21387H23.0459V5.89062H24.6279C25.1097 5.89062 25.5508 5.97363 25.9512 6.13965C26.3548 6.30241 26.7031 6.53678 26.9961 6.84277C27.2923 7.14551 27.5202 7.50521 27.6797 7.92188C27.8424 8.33529 27.9238 8.79427 27.9238 9.29883V9.59668C27.9238 10.098 27.8424 10.557 27.6797 10.9736C27.5202 11.3903 27.2939 11.75 27.001 12.0527C26.708 12.3555 26.3613 12.5898 25.9609 12.7559C25.5638 12.9186 25.1276 13 24.6523 13ZM24.0566 5.89062V13H22.3428V5.89062H24.0566Z"
        fill="black"
      />
    </ToolbarIconFrame>
  );
}

export function ARIcon(props: Props) {
  return (
    <ToolbarIconFrame className={props.className} filterId="sk-icon-ar">
      <path
        className="toolbar-icon__glyph toolbar-icon__glyph--corner"
        d="M34.6227 15.0901V12.0918C34.6227 9.68308 32.6701 7.73047 30.2614 7.73047H26.4453"
        stroke="black"
        strokeWidth="1.63548"
      />
      <path
        className="toolbar-icon__glyph toolbar-icon__glyph--corner"
        d="M26.4453 32.5867H30.2614C32.6701 32.5867 34.6227 30.6355 34.6227 28.2269C34.6227 27.0746 34.6227 25.7063 34.6227 24.1367"
        stroke="black"
        strokeWidth="1.63548"
      />
      <path
        className="toolbar-icon__glyph toolbar-icon__glyph--corner"
        d="M9.72884 25.2263L9.72884 28.2246C9.72884 30.6333 11.6815 32.5859 14.0901 32.5859L17.9062 32.5859"
        stroke="black"
        strokeWidth="1.63548"
      />
      <path
        className="toolbar-icon__glyph toolbar-icon__glyph--corner"
        d="M17.9063 7.72969L14.0901 7.72969C11.6815 7.72969 9.72884 9.68088 9.72884 12.0896C9.72884 13.2418 9.72884 14.6101 9.72884 16.1797"
        stroke="black"
        strokeWidth="1.63548"
      />
      <path
        className="toolbar-icon__glyph toolbar-icon__glyph--cube"
        d="M21.0088 11.8152C21.8265 11.3945 22.8049 11.4083 23.6124 11.8572L29.1044 14.913C29.9346 15.3743 30.4486 16.2461 30.4486 17.1912V23.2737C30.4486 24.189 29.966 25.0355 29.1811 25.5076L29.1044 25.5518L23.6124 28.6068C22.8049 29.0565 21.8266 29.0703 21.0088 28.6496L20.9299 28.6068L15.4379 25.5518C14.634 25.1043 14.1259 24.2731 14.0952 23.3621L14.0938 23.2736V17.1912C14.0938 16.2461 14.6078 15.3743 15.4379 14.9129L20.9299 11.8572L21.0088 11.8152ZM15.8461 23.2736L15.8483 23.3323C15.868 23.6246 16.0345 23.8886 16.2944 24.033L21.395 26.8703V20.6885C21.2358 20.6349 21.0796 20.5667 20.9299 20.4833L15.8461 17.6554L15.8461 23.2736ZM23.6124 20.4834C23.4627 20.5668 23.3065 20.635 23.1473 20.6887V26.8704L28.2479 24.0331L28.2991 24.0026C28.5458 23.8431 28.6962 23.5689 28.6962 23.2737V17.6554L23.6124 20.4834ZM22.756 13.3769C22.4742 13.2195 22.1332 13.2094 21.8433 13.3472L21.7864 13.3769L16.7632 16.1708L21.7864 18.9647C22.0872 19.1323 22.4552 19.1323 22.756 18.9647L27.7792 16.1708L22.756 13.3769Z"
        fill="black"
      />
    </ToolbarIconFrame>
  );
}

export function DownloadImageIcon(props: Props) {
  return (
    <ToolbarIconFrame
      className={props.className}
      filterId="sk-icon-download-image"
    >
      <path
        className="toolbar-icon__glyph"
        d="M22 27H15C14.4696 27 13.9609 26.7893 13.5858 26.4142C13.2107 26.0391 13 25.5304 13 25V16C13 15.4696 13.2107 14.9609 13.5858 14.5858C13.9609 14.2107 14.4696 14 15 14H16C16.5304 14 17.0391 13.7893 17.4142 13.4142C17.7893 13.0391 18 12.5304 18 12C18 11.7348 18.1054 11.4804 18.2929 11.2929C18.4804 11.1054 18.7348 11 19 11H25C25.2652 11 25.5196 11.1054 25.7071 11.2929C25.8946 11.4804 26 11.7348 26 12C26 12.5304 26.2107 13.0391 26.5858 13.4142C26.9609 13.7893 27.4696 14 28 14H29C29.5304 14 30.0391 14.2107 30.4142 14.5858C30.7893 14.9609 31 15.4696 31 16V19.5M29 23V29M26 26L29 29L32 26M19 20C19 20.7956 19.3161 21.5587 19.8787 22.1213C20.4413 22.6839 21.2044 23 22 23C22.7956 23 23.5587 22.6839 24.1213 22.1213C24.6839 21.5587 25 20.7956 25 20C25 19.2044 24.6839 18.4413 24.1213 17.8787C23.5587 17.3161 22.7956 17 22 17C21.2044 17 20.4413 17.3161 19.8787 17.8787C19.3161 18.4413 19 19.2044 19 20Z"
        stroke="#111111"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </ToolbarIconFrame>
  );
}
